import { skipToken } from "@reduxjs/toolkit/query";
import { Alert, Button, Skeleton, message } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import {
  Ban,
  CalendarCheck2,
  CalendarDays,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  ExternalLink,
  MapPin,
  RotateCcw,
  Users,
  Video,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  useCancelSchedulePublicBookingMutation,
  useCreateSchedulePublicBookingMutation,
  useGetSchedulePublicBookingDetailsQuery,
  useGetSchedulePublicAvailableDatesQuery,
  useGetSchedulePublicAvailableSlotsQuery,
  useGetSchedulePublicBookingLinkQuery,
  useReserveSchedulePublicSlotMutation,
  useRescheduleSchedulePublicBookingMutation,
  useSendSchedulePublicEmailVerificationMutation,
} from "@/api/scheduling/schedule-public.api";
import type {
  SchedulePublicBookingAnswerInput,
  SchedulePublicBookingData,
  SchedulePublicBookingDetails,
  SchedulePublicBookingInviteeInput,
  SchedulePublicBookingLink,
  SchedulePublicCancelBookingData,
  SchedulePublicCreateBookingInput,
  SchedulePublicAvailableSlot,
  SchedulePublicBookingHost,
  SchedulePublicJsonRecord,
} from "@/api/scheduling/schedule-public.types";
import { IMAGES } from "@/assets";
import { Input, TextAreaInput } from "@/design-system/components/input/Input";
import { Link } from "@/design-system/components/link/Link";
import { Modal } from "@/design-system/components/modal/modal";
import { colors } from "@/design-system/theme/colors";
import { AvailableSlotsPanel } from "./components/AvailableSlotsPanel";
import { BookingCalendar } from "./components/BookingCalendar";
import {
  BookingQuestionsForm,
  type PublicBookingInviteeDetails,
  type PublicBookingQuestionSubmitPayload,
  type PublicBookingQuestionValue,
} from "./components/BookingQuestionsForm";
import { EventDetailsPanel } from "./components/EventDetailsPanel";
import {
  canReschedulePublicBooking,
  canSelectBookingTimezone,
  formatDateParam,
  formatLocationLabel,
  formatSlotTime,
  getDefaultLocation,
  getLocationProviderLogo,
  getAvailabilityDateRanges,
  getBookingTimezone,
  getBrandColor,
  getBrandTint,
  getFirstAvailableDate,
  getHostName,
  getInitialBookingDate,
  getPublicBookingWindowEndDate,
  isDateInPublicBookingWindow,
  type PublicBookingTimeFormat,
} from "./utils";
import {
  clearRoutingSubmissionHandoff,
  getRoutingSubmissionFromLocation,
  getStoredRoutingSubmissionHandoff,
} from "./routingSubmissionHandoff";

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === "string" && error.trim()) return error;
  if (error instanceof Error && error.message.trim()) return error.message;
  if (!error || typeof error !== "object") return fallback;

  const data = "data" in error ? error.data : undefined;
  if (typeof data === "string" && data.trim()) return data;
  if (data && typeof data === "object") {
    if ("message" in data) {
      const message = data.message;
      if (typeof message === "string" && message.trim()) return message;
    }
    if ("error" in data) {
      const message = data.error;
      if (typeof message === "string" && message.trim()) return message;
    }
    if ("errors" in data && Array.isArray(data.errors)) {
      const firstError = data.errors.find(
        (entry): entry is string =>
          typeof entry === "string" && entry.trim().length > 0,
      );
      if (firstError) return firstError;
    }
  }

  if ("message" in error) {
    const message = error.message;
    if (typeof message === "string" && message.trim()) return message;
  }

  if ("status" in error) {
    const status = error.status;
    if (status === "FETCH_ERROR") {
      return "Unable to connect. Please check your internet and try again.";
    }
    if (status === "TIMEOUT_ERROR") {
      return "The booking request took too long. Please try again.";
    }
    if (status === 409) {
      return "This time is no longer available. Please choose another slot.";
    }
  }

  return fallback;
};

const toAnswerValue = (
  value: PublicBookingQuestionValue | undefined,
): string => {
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "boolean") return value ? "true" : "false";
  return String(value ?? "");
};

const toAnswerJson = (
  value: PublicBookingQuestionValue | undefined,
): SchedulePublicJsonRecord => {
  if (Array.isArray(value)) return { values: value };
  if (typeof value === "boolean") return { value };
  return {};
};

const getTrackingValue = (
  searchParams: URLSearchParams,
  primaryKey: string,
  fallbackKey: string,
  fallbackValue: string,
): string =>
  searchParams.get(primaryKey)?.trim() ||
  searchParams.get(fallbackKey)?.trim() ||
  fallbackValue;

const formatBookingTimeInTimezone = (
  value: string,
  timezone: string,
  timeFormat: PublicBookingTimeFormat,
): string => {
  if (!value || !timezone) return formatSlotTime(value, timeFormat);

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return formatSlotTime(value, timeFormat);

  try {
    return new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: timeFormat === "12h",
      timeZone: timezone,
    })
      .format(date)
      .toLowerCase()
      .replace(/\s/g, "");
  } catch {
    return formatSlotTime(value, timeFormat);
  }
};

const formatBookingDateInTimezone = (
  value: string,
  timezone: string,
  fallbackDate: Dayjs,
): string => {
  if (!value || !timezone) return fallbackDate.format("dddd, MMMM D, YYYY");

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return fallbackDate.format("dddd, MMMM D, YYYY");
  }

  try {
    return new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
      timeZone: timezone,
    }).format(date);
  } catch {
    return fallbackDate.format("dddd, MMMM D, YYYY");
  }
};

const PublicBookingSkeleton = () => (
  <div className="grid h-full grid-cols-1 lg:grid-cols-[300px_minmax(480px,1fr)_300px]">
    <div
      className="border-b p-5 sm:p-6 lg:border-b-0 lg:border-r"
      style={{ borderColor: colors.surfaceSubtle }}
    >
      <Skeleton.Avatar active size={30} className="mb-5" />
      <Skeleton active paragraph={{ rows: 5 }} />
    </div>
    <div
      className="border-b p-5 sm:p-6 lg:border-b-0 lg:border-r"
      style={{ borderColor: colors.surfaceSubtle }}
    >
      <Skeleton active paragraph={{ rows: 7 }} />
    </div>
    <div className="p-5 sm:p-6">
      <Skeleton active paragraph={{ rows: 6 }} />
    </div>
  </div>
);

const PUBLIC_BOOKING_SCROLL_UNLOCK_CLASS =
  "public-booking-document-scroll-unlocked";

const publicBookingDocumentScrollStyles = `
  html.${PUBLIC_BOOKING_SCROLL_UNLOCK_CLASS} {
    height: auto !important;
    min-height: 100%;
    overflow-x: hidden !important;
    overflow-y: auto !important;
  }

  body.${PUBLIC_BOOKING_SCROLL_UNLOCK_CLASS} {
    height: auto !important;
    min-height: 100%;
    overflow-x: hidden !important;
    overflow-y: visible !important;
  }

  #root.${PUBLIC_BOOKING_SCROLL_UNLOCK_CLASS} {
    height: auto !important;
    min-height: 100%;
    overflow: visible !important;
  }
`;

interface ConfirmedBookingState {
  booking: SchedulePublicBookingData;
  message: string;
  selectedDate: Dayjs;
  selectedSlot: SchedulePublicAvailableSlot;
  timezone: string;
  invitee: PublicBookingInviteeDetails;
  guestEmails: string[];
}

interface PendingVerifiedBookingState {
  createBookingInput: SchedulePublicCreateBookingInput;
  selectedDate: Dayjs;
  selectedSlot: SchedulePublicAvailableSlot;
  timezone: string;
  invitee: PublicBookingInviteeDetails;
  guestEmails: string[];
  verificationTokenId: number;
  expiresAt?: string | null;
  expiresInMinutes?: number | null;
}

type PublicBookingMode = "booking" | "reschedule";

const getRescheduleBookingIdError = (bookingId: string): string | null => {
  if (!bookingId) return "Booking id is required to re-schedule this meeting.";
  if (!/^[A-Za-z0-9_-]+$/.test(bookingId)) {
    return "Booking id is invalid.";
  }
  return null;
};

const getRescheduleInviteeEmailError = (email: string): string | null => {
  if (!email) return "Invitee email is required to re-schedule this meeting.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return "Invitee email is invalid.";
  }
  return null;
};

const getBookingDetailsStartDate = (
  booking?: SchedulePublicBookingDetails,
): Dayjs | null => {
  const startTime =
    booking?.inviteeStartTime?.trim() || booking?.startTime?.trim() || "";
  if (!startTime) return null;
  const date = dayjs(startTime);
  return date.isValid() ? date : null;
};

const getPublicBookingReference = (
  booking?: Pick<SchedulePublicBookingData, "bookingUid" | "bookingId"> | null,
): string => {
  if (!booking) return "";
  return (
    booking.bookingUid?.trim() ||
    (typeof booking.bookingId !== "undefined" ? String(booking.bookingId) : "")
  );
};

const getPublicBookingResultPath = (
  publicKey: string,
  bookingId: string,
  inviteeEmail: string,
): string => {
  const query = new URLSearchParams();
  if (inviteeEmail.trim()) query.set("inviteeEmail", inviteeEmail.trim());
  const suffix = query.toString();
  return `/book/${encodeURIComponent(publicKey)}/${encodeURIComponent(
    bookingId,
  )}${suffix ? `?${suffix}` : ""}`;
};

const getPublicRescheduleResultPath = (
  publicKey: string,
  bookingId: string,
  inviteeEmail: string,
): string => {
  const query = new URLSearchParams();
  if (inviteeEmail.trim()) query.set("inviteeEmail", inviteeEmail.trim());
  const suffix = query.toString();
  return `/reschedule/${encodeURIComponent(publicKey)}/${encodeURIComponent(
    bookingId,
  )}${suffix ? `?${suffix}` : ""}`;
};

const getPublicBookingFallbackPath = (
  publicKey: string,
  bookingId: string,
  inviteeEmail: string,
): string =>
  bookingId
    ? getPublicBookingResultPath(publicKey, bookingId, inviteeEmail)
    : `/book/${encodeURIComponent(publicKey)}`;

const mapBookingDetailsToConfirmation = (
  details: SchedulePublicBookingDetails,
  fallbackTimezone: string,
): ConfirmedBookingState | null => {
  const bookingReference =
    details.bookingUid?.trim() ||
    (typeof details.bookingId !== "undefined" ? String(details.bookingId) : "");
  if (!bookingReference) return null;

  const startTime =
    details.inviteeStartTime?.trim() || details.startTime?.trim() || "";
  const endTime = details.inviteeEndTime?.trim() || details.endTime?.trim() || "";
  const selectedDate = dayjs(startTime || details.startTime);
  const invitees = details.invitees ?? [];
  const primaryInvitee =
    details.primaryInvitee || details.invitee || invitees[0] || null;
  const inviteeEmail = primaryInvitee?.email?.trim() || "";
  const inviteeName = primaryInvitee?.name?.trim() || inviteeEmail;
  const timezone =
    details.inviteeTimezone?.trim() ||
    primaryInvitee?.timezone?.trim() ||
    fallbackTimezone;

  return {
    booking: {
      bookingUid: details.bookingUid || bookingReference,
      bookingId:
        typeof details.bookingId === "number" ? details.bookingId : undefined,
      eventTypeId: details.eventTypeId,
      primaryHostUserId: details.primaryHostUserId,
      title: details.title,
      publicKey: details.publicKey,
      inviteeTimezone: timezone,
      hostTimezone: details.hostTimezone || undefined,
      status: details.status,
      confirmationStatus: details.confirmationStatus || undefined,
      startTime: details.startTime || startTime,
      endTime: details.endTime || endTime,
      locationType: details.locationType,
      locationValue: details.locationValue,
      meetingLink: details.meetingLink,
      hosts: details.hosts,
    },
    message: "Booking details loaded.",
    selectedDate: selectedDate.isValid() ? selectedDate : dayjs(),
    selectedSlot: {
      startTime,
      endTime,
      available: false,
      availableHostIds: [],
    },
    timezone,
    invitee: {
      name: inviteeName,
      email: inviteeEmail,
      phone: primaryInvitee?.phone?.trim() || "",
    },
    guestEmails: invitees
      .map((invitee) => invitee.email?.trim() || "")
      .filter((email) => email && email !== inviteeEmail),
  };
};

const getConfirmationStatusContent = (
  bookingLink: SchedulePublicBookingLink,
  booking: SchedulePublicBookingData,
  mode: PublicBookingMode,
): {
  eyebrow: string;
  title: string;
  description: string;
  tone: "success" | "warning" | "error";
} => {
  const status = String(booking.status || "").toLowerCase();
  if (status === "cancelled") {
    return {
      eyebrow: "Booking cancelled",
      title: "Booking cancelled successfully",
      description: "This booking has been cancelled.",
      tone: "error",
    };
  }

  const confirmationStatus = String(booking.confirmationStatus || "").toLowerCase();
  const requiresManualConfirmation =
    bookingLink.confirmationMode === "manual" ||
    confirmationStatus === "pending" ||
    confirmationStatus === "manual";

  if (requiresManualConfirmation) {
    return {
      eyebrow: mode === "reschedule" ? "Reschedule submitted" : "Booking submitted",
      title:
        mode === "reschedule"
          ? "Your reschedule request has been submitted"
          : "Your booking has been submitted",
      description: `${bookingLink.workspace.displayName || bookingLink.workspace.name || "The host"} still needs to confirm or reject the booking.`,
      tone: "warning",
    };
  }

  return {
    eyebrow: mode === "reschedule" ? "Booking re-scheduled" : "Booking confirmed",
    title:
      mode === "reschedule"
        ? "Booking rescheduled successfully"
        : "Booking confirmed successfully",
    description:
      mode === "reschedule"
        ? "We sent the updated booking details to the email address you provided."
        : "We sent the booking details to the email address you provided.",
    tone: "success",
  };
};

const getTrimmedText = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

const getNumericId = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  }
  return null;
};

const getBookingHostUserId = (
  host?: SchedulePublicBookingHost | null,
): number | null =>
  getNumericId(host?.userId) ??
  getNumericId(host?.hostUserId) ??
  getNumericId(host?.user?.id);

const getBookingHostName = (
  host?: SchedulePublicBookingHost | null,
): string => {
  if (!host) return "";
  const nestedUser = host.user;
  const fullName = [
    host.userFirstName,
    host.userLastName,
    host.firstName,
    host.lastName,
    nestedUser?.firstName,
    nestedUser?.lastName,
  ]
    .map(getTrimmedText)
    .filter(Boolean)
    .join(" ");

  return (
    getTrimmedText(host.userName) ||
    getTrimmedText(host.name) ||
    getTrimmedText(nestedUser?.userName) ||
    getTrimmedText(nestedUser?.name) ||
    fullName ||
    getTrimmedText(host.userEmail) ||
    getTrimmedText(host.email) ||
    getTrimmedText(nestedUser?.email)
  );
};

const getBookingHostEmail = (
  host?: SchedulePublicBookingHost | null,
): string =>
  getTrimmedText(host?.userEmail) ||
  getTrimmedText(host?.email) ||
  getTrimmedText(host?.user?.email);

const getAssignedHostDetails = (
  bookingLink: SchedulePublicBookingLink,
  booking: SchedulePublicBookingData,
): { name: string; email: string } => {
  const bookingHosts = booking.hosts ?? [];
  const assignedHostUserId =
    getNumericId(booking.primaryHostUserId) ??
    getBookingHostUserId(bookingHosts[0]);
  const assignedBookingHost =
    bookingHosts.find((host) => getBookingHostUserId(host) === assignedHostUserId) ??
    bookingHosts[0];
  const linkedHost =
    bookingLink.hosts?.find((host) => host.userId === assignedHostUserId) ??
    bookingLink.hosts?.find(
      (host) => getNumericId(assignedBookingHost?.id) === host.id,
    ) ??
    bookingLink.hosts?.[0];
  const workspaceName =
    bookingLink.workspace.displayName?.trim() ||
    bookingLink.workspace.name?.trim() ||
    "Host";

  return {
    name:
      getBookingHostName(assignedBookingHost) ||
      getHostName(linkedHost) ||
      workspaceName,
    email:
      getBookingHostEmail(assignedBookingHost) ||
      linkedHost?.userEmail?.trim() ||
      "",
  };
};

const formatStatusLabel = (
  booking: SchedulePublicBookingData,
  mode: PublicBookingMode,
): string => {
  const confirmationStatus = String(booking.confirmationStatus || "").toLowerCase();
  if (confirmationStatus === "pending") return "Pending host confirmation";
  if (confirmationStatus === "declined") return "Declined";
  if (mode === "reschedule") return "Re-scheduled";

  const status = String(booking.status || "").toLowerCase();
  if (status === "scheduled" || status === "confirmed") return "Confirmed";
  if (!status) return "Submitted";
  return status
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const PublicBookingConfirmation = ({
  bookingLink,
  confirmation,
  timeFormat,
  mode,
  onBookAnother,
  onCancelBooking,
  isCancellingBooking,
}: {
  bookingLink: SchedulePublicBookingLink;
  confirmation: ConfirmedBookingState;
  timeFormat: PublicBookingTimeFormat;
  mode: PublicBookingMode;
  onBookAnother: () => void;
  onCancelBooking: () => void;
  isCancellingBooking: boolean;
}) => {
  const isRescheduleMode = mode === "reschedule";
  const [areGuestsVisible, setAreGuestsVisible] = useState(false);
  const brandColor = getBrandColor(bookingLink);
  const brandTint = getBrandTint(brandColor, 0.12);
  const defaultLocation = getDefaultLocation(bookingLink.locations);
  const locationLogo = getLocationProviderLogo(
    confirmation.booking.locationType || defaultLocation?.locationType,
  );
  const meetingUrl =
    confirmation.booking.meetingLink?.trim() ||
    confirmation.booking.locationValue?.trim() ||
    "";
  const isMeetingUrl = /^https?:\/\//i.test(meetingUrl);
  const locationLabel = formatLocationLabel(defaultLocation);
  const displayTimezone =
    confirmation.booking.inviteeTimezone || confirmation.timezone;
  const startTime =
    confirmation.booking.startTime || confirmation.selectedSlot.startTime;
  const endTime =
    confirmation.booking.endTime || confirmation.selectedSlot.endTime;
  const dateLabel = formatBookingDateInTimezone(
    startTime,
    displayTimezone,
    confirmation.selectedDate,
  );
  const statusContent = getConfirmationStatusContent(
    bookingLink,
    confirmation.booking,
    mode,
  );
  const toneColor =
    statusContent.tone === "error"
      ? colors.error
      : statusContent.tone === "warning"
        ? colors.warningStrong
        : brandColor;
  const toneTint =
    statusContent.tone === "error"
      ? colors.errorBgSubtle
      : statusContent.tone === "warning"
        ? colors.warningBgSubtle
        : brandTint;
  const host = getAssignedHostDetails(bookingLink, confirmation.booking);
  const guestEmails = confirmation.guestEmails.filter(Boolean);
  const inviteeName = confirmation.invitee.name || confirmation.invitee.email;
  const inviteeEmail = confirmation.invitee.email;
  const timeLabel = `${formatBookingTimeInTimezone(
    startTime,
    displayTimezone,
    timeFormat,
  )} - ${formatBookingTimeInTimezone(endTime, displayTimezone, timeFormat)}`;
  const bookingReference =
    confirmation.booking.bookingUid ||
    (typeof confirmation.booking.bookingId !== "undefined"
      ? String(confirmation.booking.bookingId)
      : "");
  const statusLabel = formatStatusLabel(confirmation.booking, mode);
  const isCancelled =
    String(confirmation.booking.status || "").toLowerCase() === "cancelled";
  const canCancelBooking =
    mode === "booking" &&
    bookingLink.allowCancellation === true &&
    Boolean(bookingReference) &&
    Boolean(inviteeEmail) &&
    !isCancelled;
  const whereLabel =
    (isMeetingUrl
      ? ""
      : defaultLocation?.locationType === "google_meet" ||
          defaultLocation?.locationType === "zoom" ||
          defaultLocation?.locationType === "microsoft_teams"
        ? "Web conferencing details will be shared in the confirmation email."
        : locationLabel) ||
    "Web conferencing details will be shared in the confirmation email.";
  const rowClassName =
    "grid gap-3 px-5 py-4 sm:grid-cols-[180px_minmax(0,1fr)] sm:px-7";
  const rowLabelClassName =
    "flex items-center gap-3 text-[15px] font-semibold";
  const rowValueClassName = "min-w-0 text-[15px] leading-6";
  const dividerStyle = { borderColor: colors.surfaceLight };

  return (
    <div className="flex flex-col items-center px-2 pb-3 pt-0 sm:px-6 sm:pb-4">
      <div
        className="w-full max-w-210 overflow-hidden rounded-xl border text-left shadow-[0_12px_34px_rgba(25,38,72,0.06)]"
        style={{
          borderColor: colors.surfaceSubtle,
          backgroundColor: colors.surfaceLighter,
        }}
      >
        <div className="px-5 pb-7 pt-8 text-center sm:px-8 sm:pb-8 sm:pt-9">
          <div
            className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full"
            style={{ backgroundColor: toneTint, color: toneColor }}
          >
            <CheckCircle2 size={30} strokeWidth={2.4} />
          </div>
          <h1
            className="text-[22px] font-semibold leading-tight tracking-normal sm:text-[28px]"
            style={{ color: colors.textDarkest }}
          >
            {statusContent.title}
          </h1>
          <p
            className="mx-auto mt-3 max-w-140 text-[15px] leading-6"
            style={{ color: colors.textDefault }}
          >
            {statusContent.description}
          </p>
        </div>

        <div className="bg-white">
          <div className={rowClassName}>
            <div
              className={rowLabelClassName}
              style={{ color: colors.textDarkest }}
            >
              <CalendarCheck2 size={19} style={{ color: brandColor }} />
              <span>What</span>
            </div>
            <div
              className={rowValueClassName}
              style={{ color: colors.textDarkest }}
            >
              {bookingLink.confirmationMode === "manual"
                ? `Manual confirmation for ${bookingLink.name}`
                : bookingLink.name}
            </div>
          </div>

          <div
            className={`${rowClassName} border-t`}
            style={dividerStyle}
          >
            <div
              className={rowLabelClassName}
              style={{ color: colors.textDarkest }}
            >
              <CalendarDays size={19} style={{ color: brandColor }} />
              <span>When</span>
            </div>
            <div
              className={rowValueClassName}
              style={{ color: colors.textDarkest }}
            >
              <div>{dateLabel}</div>
              <div>
                {timeLabel}{" "}
                <span style={{ color: colors.textMuted }}>({displayTimezone})</span>
              </div>
            </div>
          </div>

          <div
            className={`${rowClassName} border-t`}
            style={dividerStyle}
          >
            <div
              className={rowLabelClassName}
              style={{ color: colors.textDarkest }}
            >
              <Users size={19} style={{ color: brandColor }} />
              <span>Who</span>
            </div>
            <div
              className="space-y-3 text-[15px] leading-6"
              style={{ color: colors.textDarkest }}
            >
              <div className="space-y-0.5">
                <div className="flex flex-wrap items-center gap-2 font-medium">
                  <span>{host.name}</span>
                  <span
                    className="rounded-md px-2 py-0.5 text-xs font-semibold"
                    style={{
                      backgroundColor: brandTint,
                      color: brandColor,
                    }}
                  >
                    Host
                  </span>
                </div>
                {host.email ? (
                  <div className="mt-1" style={{ color: colors.textMuted }}>
                    {host.email}
                  </div>
                ) : null}
              </div>
              <div className="space-y-0.5">
                <div className="font-medium">{inviteeName}</div>
                {inviteeEmail ? (
                  <div className="mt-1" style={{ color: colors.textMuted }}>
                    {inviteeEmail}
                  </div>
                ) : null}
              </div>
              {guestEmails.length > 0 ? (
                <div className="pt-1">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold"
                    style={{ color: brandColor }}
                    onClick={() => setAreGuestsVisible((current) => !current)}
                    aria-expanded={areGuestsVisible}
                  >
                    <span>
                      {areGuestsVisible ? "Hide guests" : "Show guests"} (
                      {guestEmails.length})
                    </span>
                    {areGuestsVisible ? (
                      <ChevronUp size={16} aria-hidden />
                    ) : (
                      <ChevronDown size={16} aria-hidden />
                    )}
                  </button>
                  {areGuestsVisible ? (
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      {guestEmails.map((email, index) => (
                        <div key={email} className="min-w-0">
                          <div className="text-sm font-medium">
                            Guest {index + 1}
                          </div>
                          <div
                            className="mt-0.5 truncate text-sm"
                            style={{ color: colors.textMuted }}
                            title={email}
                          >
                            {email}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>

          <div
            className={`${rowClassName} border-t`}
            style={dividerStyle}
          >
            <div
              className={rowLabelClassName}
              style={{ color: colors.textDarkest }}
            >
              <span className="flex h-5 w-5 items-center justify-center">
                {locationLogo ? (
                  <img
                    src={locationLogo}
                    alt=""
                    aria-hidden
                    className="h-5 w-5 object-contain"
                    loading="lazy"
                    decoding="async"
                  />
                ) : meetingUrl ? (
                  <Video size={19} style={{ color: brandColor }} />
                ) : (
                  <MapPin size={19} style={{ color: brandColor }} />
                )}
              </span>
              <span>Where</span>
            </div>
            <div
              className={`${rowValueClassName} break-words`}
              style={{ color: colors.textDarkest }}
            >
              {isMeetingUrl ? (
                <Link
                  href={meetingUrl}
                  external
                  size="medium"
                  weight={600}
                  underline="hover"
                  className="inline-flex items-center gap-2 align-middle"
                >
                  <span>Open meeting link</span>
                  <ExternalLink size={16} aria-hidden />
                </Link>
              ) : (
                whereLabel
              )}
            </div>
          </div>

          <div
            className={`${rowClassName} border-t`}
            style={dividerStyle}
          >
            <div
              className={rowLabelClassName}
              style={{ color: colors.textDarkest }}
            >
              <CheckCircle2 size={19} style={{ color: toneColor }} />
              <span>Status</span>
            </div>
            <div className={rowValueClassName} style={{ color: colors.textDarkest }}>
              <div className="font-semibold" style={{ color: toneColor }}>
                {statusLabel}
              </div>
              {bookingReference ? (
                <div className="text-sm" style={{ color: colors.textMuted }}>
                  Reference: {bookingReference}
                </div>
              ) : null}
            </div>
          </div>

          <div
            className="flex flex-col gap-2 border-t px-5 py-4 sm:flex-row sm:justify-end sm:px-7"
            style={dividerStyle}
          >
            {canCancelBooking ? (
              <Button
                danger
                size="middle"
                icon={<Ban size={16} />}
                loading={isCancellingBooking}
                disabled={isCancellingBooking}
                onClick={onCancelBooking}
              >
                Cancel booking
              </Button>
            ) : null}
            <Button
              size="middle"
              icon={<RotateCcw size={16} />}
              onClick={onBookAnother}
            >
              {isRescheduleMode ? "Choose another time" : "Book another"}
            </Button>
            {isMeetingUrl ? (
              <Button
                type="primary"
                size="middle"
                icon={<ExternalLink size={16} />}
                href={meetingUrl}
                target="_blank"
                rel="noreferrer"
                style={{ backgroundColor: brandColor, borderColor: brandColor }}
              >
                Open link
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

const PublicBookingPage = () => {
  const { publicKey, bookingId: bookingIdParam } = useParams<{
    publicKey: string;
    bookingId?: string;
  }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isReschedulePath = location.pathname
    .toLowerCase()
    .split("/")
    .filter(Boolean)
    .includes("reschedule");
  const mode: PublicBookingMode = isReschedulePath ? "reschedule" : "booking";
  const isRescheduleMode = mode === "reschedule";
  const routingSubmissionHandoff = useMemo(() => {
    if (isRescheduleMode || !publicKey) return null;
    return (
      getRoutingSubmissionFromLocation(location, publicKey) ??
      getStoredRoutingSubmissionHandoff(publicKey)
    );
  }, [isRescheduleMode, location, publicKey]);
  const routingFormSubmissionId =
    routingSubmissionHandoff?.submissionId ?? null;
  const bookingResultId = useMemo(
    () =>
      !isRescheduleMode
        ? bookingIdParam?.trim() ||
          searchParams.get("bookingId")?.trim() ||
          searchParams.get("bookingUid")?.trim() ||
          searchParams.get("booking")?.trim() ||
          ""
        : "",
    [bookingIdParam, isRescheduleMode, searchParams],
  );
  const rescheduleBookingId = useMemo(
    () =>
      isRescheduleMode
        ? bookingIdParam?.trim() ||
          searchParams.get("bookingId")?.trim() ||
          searchParams.get("bookingUid")?.trim() ||
          searchParams.get("booking")?.trim() ||
          ""
        : "",
    [bookingIdParam, isRescheduleMode, searchParams],
  );
  const rescheduleBookingIdError = useMemo(
    () =>
      isRescheduleMode
        ? getRescheduleBookingIdError(rescheduleBookingId)
        : null,
    [isRescheduleMode, rescheduleBookingId],
  );
  const rescheduleInviteeEmail = useMemo(
    () =>
      isRescheduleMode
        ? searchParams.get("inviteeEmail")?.trim() ||
          searchParams.get("email")?.trim() ||
          ""
        : "",
    [isRescheduleMode, searchParams],
  );
  const rescheduleInviteeEmailError = useMemo(
    () =>
      isRescheduleMode
        ? getRescheduleInviteeEmailError(rescheduleInviteeEmail)
        : null,
    [isRescheduleMode, rescheduleInviteeEmail],
  );
  const bookingResultInviteeEmail = useMemo(
    () =>
      !isRescheduleMode
        ? searchParams.get("inviteeEmail")?.trim() ||
          searchParams.get("email")?.trim() ||
          ""
        : "",
    [isRescheduleMode, searchParams],
  );
  const bookingResultInviteeEmailError = useMemo(
    () =>
      !isRescheduleMode && bookingResultId
        ? getRescheduleInviteeEmailError(bookingResultInviteeEmail)
        : null,
    [bookingResultId, bookingResultInviteeEmail, isRescheduleMode],
  );
  const [dateState, setDateState] = useState<{
    publicKey?: string;
    selectedDate?: string;
    visibleMonth?: string;
  }>({});

  const bookingLinkInput = useMemo(() => {
    if (!publicKey) return skipToken;
    return { publicKey };
  }, [publicKey]);

  const {
    data: bookingLink,
    isLoading: isBookingLinkLoading,
    isFetching: isBookingLinkFetching,
    error: bookingLinkError,
  } = useGetSchedulePublicBookingLinkQuery(bookingLinkInput);
  const isRescheduleAllowedByLink = !isRescheduleMode
    ? true
    : canReschedulePublicBooking(bookingLink);
  const rescheduleUnavailableMessage =
    "Re-scheduling is not available for this booking.";

  const bookingDetailsInput = useMemo(() => {
    if (
      !isRescheduleMode ||
      !publicKey ||
      !bookingLink ||
      !isRescheduleAllowedByLink ||
      rescheduleBookingIdError ||
      rescheduleInviteeEmailError
    ) {
      return skipToken;
    }
    return {
      publicKey,
      bookingId: rescheduleBookingId,
      inviteeEmail: rescheduleInviteeEmail,
    };
  }, [
    isRescheduleMode,
    bookingLink,
    isRescheduleAllowedByLink,
    publicKey,
    rescheduleBookingId,
    rescheduleBookingIdError,
    rescheduleInviteeEmail,
    rescheduleInviteeEmailError,
  ]);

  const {
    data: rescheduleBookingDetails,
    isLoading: isBookingDetailsLoading,
    isFetching: isBookingDetailsFetching,
    error: bookingDetailsError,
  } = useGetSchedulePublicBookingDetailsQuery(bookingDetailsInput);
  const isRescheduleAllowedForBooking = !isRescheduleMode
    ? true
    : canReschedulePublicBooking(bookingLink, rescheduleBookingDetails);

  useEffect(() => {
    if (
      !isRescheduleMode ||
      !publicKey ||
      !bookingLink ||
      isRescheduleAllowedByLink
    ) {
      return;
    }

    navigate(
      getPublicBookingFallbackPath(
        publicKey,
        rescheduleBookingIdError ? "" : rescheduleBookingId,
        rescheduleInviteeEmailError ? "" : rescheduleInviteeEmail,
      ),
      { replace: true },
    );
  }, [
    bookingLink,
    isRescheduleAllowedByLink,
    isRescheduleMode,
    navigate,
    publicKey,
    rescheduleBookingId,
    rescheduleBookingIdError,
    rescheduleInviteeEmail,
    rescheduleInviteeEmailError,
  ]);

  useEffect(() => {
    if (
      !isRescheduleMode ||
      !publicKey ||
      !bookingLink ||
      !rescheduleBookingDetails ||
      isRescheduleAllowedForBooking
    ) {
      return;
    }

    navigate(
      getPublicBookingFallbackPath(
        publicKey,
        rescheduleBookingIdError ? "" : rescheduleBookingId,
        rescheduleInviteeEmailError ? "" : rescheduleInviteeEmail,
      ),
      { replace: true },
    );
  }, [
    bookingLink,
    isRescheduleAllowedForBooking,
    isRescheduleMode,
    navigate,
    publicKey,
    rescheduleBookingDetails,
    rescheduleBookingId,
    rescheduleBookingIdError,
    rescheduleInviteeEmail,
    rescheduleInviteeEmailError,
  ]);

  const bookingResultDetailsInput = useMemo(() => {
    if (
      isRescheduleMode ||
      !publicKey ||
      !bookingResultId ||
      bookingResultInviteeEmailError
    ) {
      return skipToken;
    }
    return {
      publicKey,
      bookingId: bookingResultId,
      inviteeEmail: bookingResultInviteeEmail,
    };
  }, [
    bookingResultId,
    bookingResultInviteeEmail,
    bookingResultInviteeEmailError,
    isRescheduleMode,
    publicKey,
  ]);

  const {
    data: bookingResultDetails,
    isLoading: isBookingResultDetailsLoading,
    isFetching: isBookingResultDetailsFetching,
    error: bookingResultDetailsError,
  } = useGetSchedulePublicBookingDetailsQuery(bookingResultDetailsInput);

  const canSelectTimezone = canSelectBookingTimezone(bookingLink);
  const defaultTimezone = useMemo(
    () => getBookingTimezone(bookingLink),
    [bookingLink],
  );
  // Invitee-chosen timezone override. Null until the visitor picks one, so the
  // booking link's own timezone rules remain the source of truth by default.
  const [selectedTimezone, setSelectedTimezone] = useState<string | null>(null);
  const timezone =
    canSelectTimezone && selectedTimezone ? selectedTimezone : defaultTimezone;
  const brandLogoUrl = bookingLink?.workspace?.logoUrl?.trim() || "";
  const brandName =
    bookingLink?.workspace?.displayName?.trim() ||
    bookingLink?.workspace?.name?.trim() ||
    "";
  const initialDate = useMemo(
    () =>
      getBookingDetailsStartDate(rescheduleBookingDetails) ||
      getInitialBookingDate(bookingLink),
    [bookingLink, rescheduleBookingDetails],
  );
  const [timeFormat, setTimeFormat] = useState<PublicBookingTimeFormat>("12h");
  const [selectedSlotKey, setSelectedSlotKey] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] =
    useState<SchedulePublicAvailableSlot | null>(null);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [confirmedBooking, setConfirmedBooking] =
    useState<ConfirmedBookingState | null>(null);
  const [pendingVerifiedBooking, setPendingVerifiedBooking] =
    useState<PendingVerifiedBookingState | null>(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationCodeError, setVerificationCodeError] = useState<
    string | null
  >(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelReasonError, setCancelReasonError] = useState<string | null>(
    null,
  );
  const [messageApi, contextHolder] = message.useMessage();

  const isCurrentDateState = dateState.publicKey === publicKey;
  const selectedDate = useMemo(
    () => {
      if (isCurrentDateState && dateState.selectedDate) {
        const storedDate = dayjs(dateState.selectedDate).startOf("day");
        if (
          storedDate.isValid() &&
          isDateInPublicBookingWindow(storedDate, bookingLink)
        ) {
          return storedDate;
        }
      }
      return initialDate;
    },
    [bookingLink, dateState.selectedDate, initialDate, isCurrentDateState],
  );
  const visibleMonth = useMemo(
    () => {
      const selectedMonth = selectedDate.startOf("month");
      if (!isCurrentDateState || !dateState.visibleMonth) return selectedMonth;

      const storedMonth = dayjs(dateState.visibleMonth).startOf("month");
      if (!storedMonth.isValid()) return selectedMonth;

      const minimumMonth = getInitialBookingDate(bookingLink).startOf("month");
      const maximumMonth = getPublicBookingWindowEndDate(bookingLink)?.startOf(
        "month",
      );

      if (storedMonth.isBefore(minimumMonth, "month")) return selectedMonth;
      if (maximumMonth && storedMonth.isAfter(maximumMonth, "month")) {
        return selectedMonth;
      }

      return storedMonth;
    },
    [
      bookingLink,
      dateState.visibleMonth,
      isCurrentDateState,
      selectedDate,
    ],
  );

  // The backend caps each availability request at 62 days, so we request a few
  // contiguous chunks to cover ~6 months and merge them into a single set.
  const availabilityRanges = useMemo(
    () => getAvailabilityDateRanges(visibleMonth, bookingLink),
    [bookingLink, visibleMonth],
  );
  const availabilityInputs = useMemo(() => {
    const toInput = (range: { from: string; to: string } | undefined) => {
      if (
        !publicKey ||
        !bookingLink ||
        !range ||
        !isRescheduleAllowedForBooking
      ) {
        return skipToken;
      }
      return { publicKey, from: range.from, to: range.to, timezone };
    };
    return [
      toInput(availabilityRanges[0]),
      toInput(availabilityRanges[1]),
      toInput(availabilityRanges[2]),
    ] as const;
  }, [
    availabilityRanges,
    bookingLink,
    isRescheduleAllowedForBooking,
    publicKey,
    timezone,
  ]);

  const availableDatesChunk0 = useGetSchedulePublicAvailableDatesQuery(
    availabilityInputs[0],
  );
  const availableDatesChunk1 = useGetSchedulePublicAvailableDatesQuery(
    availabilityInputs[1],
  );
  const availableDatesChunk2 = useGetSchedulePublicAvailableDatesQuery(
    availabilityInputs[2],
  );

  const availableDatesChunks = [
    availableDatesChunk0,
    availableDatesChunk1,
    availableDatesChunk2,
  ];
  const isAvailableDatesLoading = availableDatesChunks.some(
    (chunk) => chunk.isLoading,
  );
  const isAvailableDatesFetching = availableDatesChunks.some(
    (chunk) => chunk.isFetching,
  );
  const isAvailabilityWindowComplete =
    !isAvailableDatesFetching &&
    availableDatesChunks.every((chunk) => chunk.isSuccess || chunk.isError);

  const availableDatesSet = useMemo(() => {
    const dates = [
      availableDatesChunk0.data?.availableDates,
      availableDatesChunk1.data?.availableDates,
      availableDatesChunk2.data?.availableDates,
    ];
    // Still loading: no chunk has resolved yet → fall back to window rules.
    if (dates.every((entry) => entry === undefined)) return null;
    return new Set(dates.flatMap((entry) => entry ?? []));
  }, [
    availableDatesChunk0.data,
    availableDatesChunk1.data,
    availableDatesChunk2.data,
  ]);
  const completedAvailabilityDatesSet = useMemo(() => {
    if (!isAvailabilityWindowComplete) return null;
    return new Set([
      ...(availableDatesChunk0.data?.availableDates ?? []),
      ...(availableDatesChunk1.data?.availableDates ?? []),
      ...(availableDatesChunk2.data?.availableDates ?? []),
    ]);
  }, [
    availableDatesChunk0.data,
    availableDatesChunk1.data,
    availableDatesChunk2.data,
    isAvailabilityWindowComplete,
  ]);
  const selectedDateParam = formatDateParam(selectedDate);
  const selectedDateHasAvailability = Boolean(
    availableDatesSet?.has(selectedDateParam),
  );

  // When the booking opens on a date with no availability (e.g. today is a
  // weekend), snap the selection to the first available date so the slots
  // panel isn't empty. Only runs before the visitor has picked a date.
  useEffect(() => {
    if (
      !publicKey ||
      isCurrentDateState ||
      !completedAvailabilityDatesSet
    ) {
      return;
    }
    if (completedAvailabilityDatesSet.has(formatDateParam(initialDate))) return;

    const firstAvailable = getFirstAvailableDate(completedAvailabilityDatesSet);
    if (!firstAvailable) return;

    setDateState({
      publicKey,
      selectedDate: formatDateParam(firstAvailable),
      visibleMonth: formatDateParam(firstAvailable.startOf("month")),
    });
  }, [
    completedAvailabilityDatesSet,
    initialDate,
    isCurrentDateState,
    publicKey,
  ]);

  useEffect(() => {
    setSelectedSlotKey(null);
    setSelectedSlot(null);
    setSubmissionError(null);
    setConfirmedBooking(null);
    setSelectedTimezone(null);
    setPendingVerifiedBooking(null);
    setVerificationCode("");
    setVerificationCodeError(null);
  }, [isRescheduleMode, publicKey, rescheduleBookingId]);

  useEffect(() => {
    if (!bookingResultDetails || !bookingResultId) return;

    const confirmation = mapBookingDetailsToConfirmation(
      bookingResultDetails,
      timezone,
    );
    if (!confirmation) return;

    setConfirmedBooking(confirmation);
    setSelectedSlotKey(null);
    setSelectedSlot(null);
    setSubmissionError(null);
    setPendingVerifiedBooking(null);
    setVerificationCode("");
    setVerificationCodeError(null);
    if (publicKey) clearRoutingSubmissionHandoff(publicKey);
  }, [bookingResultDetails, bookingResultId, publicKey, timezone]);

  useEffect(() => {
    if (!isRescheduleMode || !publicKey || !rescheduleBookingDetails) return;
    const bookingStartDate = getBookingDetailsStartDate(rescheduleBookingDetails);
    if (!bookingStartDate) return;

    setDateState({
      publicKey,
      selectedDate: formatDateParam(bookingStartDate),
      visibleMonth: formatDateParam(bookingStartDate.startOf("month")),
    });
  }, [isRescheduleMode, publicKey, rescheduleBookingDetails]);

  useEffect(() => {
    const root = document.getElementById("root");
    const targets = [document.documentElement, document.body, root].filter(
      (target): target is HTMLElement => Boolean(target),
    );

    if (confirmedBooking) {
      targets.forEach((target) =>
        target.classList.add(PUBLIC_BOOKING_SCROLL_UNLOCK_CLASS),
      );
    } else {
      targets.forEach((target) =>
        target.classList.remove(PUBLIC_BOOKING_SCROLL_UNLOCK_CLASS),
      );
    }

    return () => {
      targets.forEach((target) =>
        target.classList.remove(PUBLIC_BOOKING_SCROLL_UNLOCK_CLASS),
      );
    };
  }, [confirmedBooking]);

  const slotsInput = useMemo(() => {
    if (
      !publicKey ||
      !bookingLink ||
      !isRescheduleAllowedForBooking ||
      !selectedDateHasAvailability
    ) {
      return skipToken;
    }
    return {
      publicKey,
      date: selectedDateParam,
      timezone,
    };
  }, [
    bookingLink,
    isRescheduleAllowedForBooking,
    publicKey,
    selectedDateHasAvailability,
    selectedDateParam,
    timezone,
  ]);

  const {
    data: availableSlots,
    isLoading: isSlotsLoading,
    isFetching: isSlotsFetching,
    error: slotsError,
  } = useGetSchedulePublicAvailableSlotsQuery(slotsInput);
  const [reserveSlot, { isLoading: isReservingSlot }] =
    useReserveSchedulePublicSlotMutation();
  const [createBooking, { isLoading: isCreatingBooking }] =
    useCreateSchedulePublicBookingMutation();
  const [sendEmailVerification, { isLoading: isSendingEmailVerification }] =
    useSendSchedulePublicEmailVerificationMutation();
  const [rescheduleBooking, { isLoading: isReschedulingBooking }] =
    useRescheduleSchedulePublicBookingMutation();
  const [cancelBooking, { isLoading: isCancellingBooking }] =
    useCancelSchedulePublicBookingMutation();
  const verificationExpiryLabel =
    pendingVerifiedBooking?.expiresInMinutes &&
    pendingVerifiedBooking.expiresInMinutes > 0
      ? `It expires in ${pendingVerifiedBooking.expiresInMinutes} minute${
          pendingVerifiedBooking.expiresInMinutes === 1 ? "" : "s"
        }.`
      : pendingVerifiedBooking?.expiresAt
        ? `It expires at ${pendingVerifiedBooking.expiresAt}.`
        : "";

  const handleTimezoneChange = (nextTimezone: string) => {
    const value = nextTimezone?.trim();
    if (!value || !canSelectTimezone || value === timezone) return;
    // Slot timestamps are timezone-relative, so any in-progress selection is
    // stale once the timezone changes; clear it and let the slots refetch.
    setSelectedTimezone(value);
    setSelectedSlotKey(null);
    setSelectedSlot(null);
    setSubmissionError(null);
    setPendingVerifiedBooking(null);
    setVerificationCode("");
    setVerificationCodeError(null);
  };

  const handleDateSelect = (date: Dayjs) => {
    setSelectedSlotKey(null);
    setSelectedSlot(null);
    setSubmissionError(null);
    setConfirmedBooking(null);
    setPendingVerifiedBooking(null);
    setVerificationCode("");
    setVerificationCodeError(null);
    setDateState({
      publicKey,
      selectedDate: formatDateParam(date),
      visibleMonth: formatDateParam(date.startOf("month")),
    });
  };

  const handleSlotSelect = (slot: SchedulePublicAvailableSlot) => {
    const slotKey = `${slot.startTime}-${slot.endTime}`;
    setSelectedSlotKey(slotKey);
    setSelectedSlot(slot);
    setSubmissionError(null);
    setPendingVerifiedBooking(null);
    setVerificationCode("");
    setVerificationCodeError(null);
  };

  const handleBackToSlots = () => {
    setSelectedSlot(null);
    setSubmissionError(null);
    setPendingVerifiedBooking(null);
    setVerificationCode("");
    setVerificationCodeError(null);
  };

  const completeCreateBooking = async (
    pendingBooking: Omit<
      PendingVerifiedBookingState,
      "verificationTokenId" | "expiresAt" | "expiresInMinutes"
    >,
    verification?: { verificationTokenId: number; verificationCode: string },
  ) => {
    const bookingResult = await createBooking({
      ...pendingBooking.createBookingInput,
      ...(verification
        ? {
            verificationTokenId: verification.verificationTokenId,
            verificationCode: verification.verificationCode,
          }
        : {}),
    }).unwrap();

    setConfirmedBooking({
      booking: bookingResult.booking,
      message: bookingResult.message || "Booking created successfully",
      selectedDate: pendingBooking.selectedDate,
      selectedSlot: pendingBooking.selectedSlot,
      timezone: pendingBooking.timezone,
      invitee: pendingBooking.invitee,
      guestEmails: pendingBooking.guestEmails,
    });
    setSelectedSlotKey(null);
    setSelectedSlot(null);
    setPendingVerifiedBooking(null);
    setVerificationCode("");
    setVerificationCodeError(null);
    clearRoutingSubmissionHandoff(pendingBooking.createBookingInput.publicKey);
    const bookingReference = getPublicBookingReference(bookingResult.booking);
    if (bookingReference) {
      navigate(
        getPublicBookingResultPath(
          pendingBooking.createBookingInput.publicKey,
          bookingReference,
          pendingBooking.invitee.email,
        ),
        { replace: true },
      );
    }
    messageApi.success(bookingResult.message || "Booking confirmed.");
  };

  const resendVerificationCode = async () => {
    if (!publicKey || !pendingVerifiedBooking) return;

    setVerificationCodeError(null);
    try {
      const verification = await sendEmailVerification({
        publicKey,
        email: pendingVerifiedBooking.invitee.email,
      }).unwrap();

      setPendingVerifiedBooking((current) =>
        current
          ? {
              ...current,
              verificationTokenId: verification.verificationTokenId,
              expiresAt: verification.expiresAt,
              expiresInMinutes: verification.expiresInMinutes,
            }
          : current,
      );
      setVerificationCode("");
      messageApi.success("Verification code sent.");
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Unable to send a verification code. Please try again.",
      );
      setVerificationCodeError(errorMessage);
      messageApi.error(errorMessage);
    }
  };

  const handleVerificationConfirm = async () => {
    if (!pendingVerifiedBooking) return;

    const code = verificationCode.trim();
    if (!code) {
      setVerificationCodeError("Enter the verification code sent to your email.");
      return;
    }
    if (code.length > 32) {
      setVerificationCodeError("Verification code must be 32 characters or fewer.");
      return;
    }

    setVerificationCodeError(null);
    try {
      await completeCreateBooking(pendingVerifiedBooking, {
        verificationTokenId: pendingVerifiedBooking.verificationTokenId,
        verificationCode: code,
      });
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Unable to verify this code. Please check the code and try again.",
      );
      setVerificationCodeError(errorMessage);
      messageApi.error(errorMessage);
    }
  };

  const handleConfirmBooking = async (
    payload: PublicBookingQuestionSubmitPayload,
  ) => {
    if (!publicKey || !bookingLink || !selectedSlot) return;
    setSubmissionError(null);
    const rescheduleInputError =
      rescheduleBookingIdError || rescheduleInviteeEmailError;
    if (isRescheduleMode && rescheduleInputError) {
      setSubmissionError(rescheduleInputError);
      messageApi.error(rescheduleInputError);
      return;
    }
    if (isRescheduleMode && !isRescheduleAllowedForBooking) {
      setSubmissionError(rescheduleUnavailableMessage);
      messageApi.error(rescheduleUnavailableMessage);
      navigate(
        getPublicBookingFallbackPath(
          publicKey,
          rescheduleBookingIdError ? "" : rescheduleBookingId,
          rescheduleInviteeEmailError ? "" : rescheduleInviteeEmail,
        ),
        { replace: true },
      );
      return;
    }
    const source = getTrackingValue(
      searchParams,
      "source",
      "utm_source",
      "public_booking_page",
    );
    const medium = getTrackingValue(
      searchParams,
      "medium",
      "utm_medium",
      "web",
    );
    const campaign = getTrackingValue(
      searchParams,
      "campaign",
      "utm_campaign",
      "",
    );
    try {
      const reservedSlot = await reserveSlot({
        publicKey,
        date: selectedDateParam,
        startTime: selectedSlot.startTime,
        endTime: selectedSlot.endTime,
        timezone,
        source,
        medium,
        campaign,
        inviteeEmail: payload.invitee.email,
      }).unwrap();

      if (!reservedSlot.lockKey) {
        throw new Error("Reserved slot lock key is missing.");
      }

      if (isRescheduleMode) {
        const rescheduleResult = await rescheduleBooking({
          publicKey,
          bookingId: rescheduleBookingId,
          newLockKey: reservedSlot.lockKey,
          inviteeEmail: payload.invitee.email,
          reason: payload.rescheduleReason || "",
          source,
          medium,
          campaign,
        }).unwrap();

        setConfirmedBooking({
          booking: rescheduleResult.booking,
          message:
            rescheduleResult.message || "Booking re-scheduled successfully",
          selectedDate,
          selectedSlot,
          timezone,
          invitee: payload.invitee,
          guestEmails: payload.guestEmails,
        });
        setSelectedSlotKey(null);
        setSelectedSlot(null);
        const bookingReference = getPublicBookingReference(
          rescheduleResult.booking,
        );
        if (bookingReference) {
          navigate(
            getPublicRescheduleResultPath(
              publicKey,
              bookingReference,
              payload.invitee.email,
            ),
            { replace: true },
          );
        }
        messageApi.success(
          rescheduleResult.message || "Booking re-scheduled.",
        );
        return;
      }

      const invitees: SchedulePublicBookingInviteeInput[] = [
        {
          name: payload.invitee.name,
          email: payload.invitee.email,
          phone: payload.invitee.phone,
          timezone,
          crmContactId: null,
          metadata: {},
        },
        ...payload.guestEmails.map((email) => ({
          name: "",
          email,
          phone: "",
          timezone,
          crmContactId: null,
          metadata: { guest: true },
        })),
      ];
      const answers: SchedulePublicBookingAnswerInput[] = bookingLink.questions
        .filter((question) => question.isHidden !== true)
        .sort(
          (first, second) => (first.sortOrder || 0) - (second.sortOrder || 0),
        )
        .map((question) => {
          const answerValue =
            payload.answers[question.fieldKey || String(question.id)];
          return {
            questionId: question.id,
            fieldKey: question.fieldKey,
            answerValue: toAnswerValue(answerValue),
            answerJson: toAnswerJson(answerValue),
          };
        });

      const createBookingInput: SchedulePublicCreateBookingInput = {
        publicKey,
        lockKey: reservedSlot.lockKey,
        inviteeTimezone: timezone,
        source,
        medium,
        campaign,
        bookingChannel: "public_booking_page",
        routingFormSubmissionId,
        invitees,
        answers,
      };

      const pendingBooking = {
        createBookingInput,
        selectedDate,
        selectedSlot,
        timezone,
        invitee: payload.invitee,
        guestEmails: payload.guestEmails,
      };

      if (bookingLink.requireEmailVerification) {
        const verification = await sendEmailVerification({
          publicKey,
          email: payload.invitee.email,
        }).unwrap();

        setPendingVerifiedBooking({
          ...pendingBooking,
          verificationTokenId: verification.verificationTokenId,
          expiresAt: verification.expiresAt,
          expiresInMinutes: verification.expiresInMinutes,
        });
        setVerificationCode("");
        setVerificationCodeError(null);
        messageApi.success("Verification code sent.");
        return;
      }

      await completeCreateBooking(pendingBooking);
    } catch (bookingError) {
      const errorMessage = getErrorMessage(
        bookingError,
        isRescheduleMode
          ? "Something went wrong while re-scheduling this booking. Please try again."
          : "Something went wrong while confirming this booking. Please try again.",
      );
      setSelectedSlotKey(null);
      setSubmissionError(errorMessage);
      messageApi.error(errorMessage);
    }
  };

  const openCancelModal = () => {
    setCancelReason("");
    setCancelReasonError(null);
    setIsCancelModalOpen(true);
  };

  const closeCancelModal = () => {
    if (isCancellingBooking) return;
    setIsCancelModalOpen(false);
    setCancelReason("");
    setCancelReasonError(null);
  };

  const applyCancelledBooking = (
    cancelledBooking: SchedulePublicCancelBookingData,
  ) => {
    setConfirmedBooking((current) => {
      if (!current) return current;
      return {
        ...current,
        booking: {
          ...current.booking,
          ...cancelledBooking,
          status: cancelledBooking.status || "cancelled",
          confirmationStatus:
            cancelledBooking.confirmationStatus ||
            current.booking.confirmationStatus,
        },
        message: "Booking cancelled successfully",
      };
    });
  };

  const handleCancelBooking = async () => {
    if (!publicKey || !confirmedBooking) return;

    const bookingReference = getPublicBookingReference(confirmedBooking.booking);
    if (!bookingReference) {
      setCancelReasonError("Booking reference is missing.");
      return;
    }

    const inviteeEmail = confirmedBooking.invitee.email.trim();
    if (!inviteeEmail) {
      setCancelReasonError("Invitee email is required to cancel this booking.");
      return;
    }

    const reason = cancelReason.trim();
    if (reason.length > 500) {
      setCancelReasonError("Reason must be 500 characters or fewer.");
      return;
    }

    setCancelReasonError(null);
    try {
      const result = await cancelBooking({
        publicKey,
        bookingId: bookingReference,
        inviteeEmail,
        reason,
        source: getTrackingValue(
          searchParams,
          "source",
          "utm_source",
          "public_booking_page",
        ),
        medium: getTrackingValue(searchParams, "medium", "utm_medium", "WEB"),
        campaign: getTrackingValue(searchParams, "campaign", "utm_campaign", ""),
      }).unwrap();

      applyCancelledBooking(result.booking);
      setIsCancelModalOpen(false);
      setCancelReason("");
      messageApi.success(result.message || "Booking cancelled successfully.");
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Unable to cancel this booking. Please try again.",
      );
      setCancelReasonError(errorMessage);
      messageApi.error(errorMessage);
    }
  };

  return (
    <main
      className={`flex ${confirmedBooking ? "min-h-dvh overflow-visible" : "h-dvh overflow-hidden"} flex-col px-3 pb-3 pt-5 sm:px-4 sm:pb-4 sm:pt-7 md:px-6`}
      style={{
        background: `linear-gradient(135deg, ${colors.white} 0%, ${colors.surfaceLightest} 30%, ${colors.surfaceLight} 58%, ${colors.surfaceLighter} 78%, ${colors.white} 100%)`,
      }}
    >
      <style>{publicBookingDocumentScrollStyles}</style>
      {contextHolder}
      <div
        className={`mx-auto flex w-full max-w-290 flex-col ${
          confirmedBooking ? "min-h-dvh pb-8" : "h-full"
        }`}
      >
        {!confirmedBooking ? (
          <header className="flex h-14 shrink-0 items-center justify-start sm:h-16">
            {brandLogoUrl || brandName ? (
              <div className="inline-flex min-w-0 items-center gap-3">
                {brandLogoUrl ? (
                  <img
                    src={brandLogoUrl}
                    alt={brandName || "Workspace logo"}
                    className="h-11 w-auto max-w-45 shrink-0 object-contain"
                  />
                ) : null}
                {brandName ? (
                  <span
                    className="truncate text-xl font-semibold tracking-normal sm:text-[28px]"
                    style={{ color: colors.primaryDarkest }}
                  >
                    {brandName}
                  </span>
                ) : null}
              </div>
            ) : (
              <a
                href="/"
                aria-label="Notifyy home"
                className="inline-flex items-center gap-3"
              >
                <img
                  src={IMAGES.notifyyLogo}
                  alt="Notifyy"
                  className="h-11 w-auto object-contain"
                />
                <span
                  className="text-[28px] font-semibold tracking-normal"
                  style={{ color: colors.primaryDarkest }}
                >
                  Notifyy
                </span>
              </a>
            )}
          </header>
        ) : null}

        <div
          className={`flex flex-col items-center gap-3 sm:gap-4 ${
            confirmedBooking
              ? "min-h-0 flex-none justify-start py-1 sm:py-2"
              : "min-h-0 flex-1 justify-center py-3 sm:py-4"
          }`}
        >
          <section
            className={
              confirmedBooking
                ? "min-h-0 w-full max-w-290 flex-1 overflow-visible bg-transparent"
                : `min-h-0 w-full overflow-x-hidden overflow-y-auto overscroll-contain rounded-xl border bg-white shadow-[0_18px_46px_rgba(25,38,72,0.10)] lg:overflow-hidden ${
                    selectedSlot
                      ? "max-w-250 flex-1 lg:h-[calc(100dvh-154px)] lg:max-h-180"
                      : "max-w-290 flex-1 lg:max-h-135"
                  }`
            }
            style={{
              borderColor: confirmedBooking
                ? colors.transparent
                : colors.surfaceSubtle,
            }}
          >
            {!publicKey ? (
              <div className="p-8">
                <Alert
                  type="error"
                  showIcon
                  message={
                    isRescheduleMode
                      ? "Reschedule link is missing."
                      : "Booking link is missing."
                  }
                />
              </div>
            ) : isBookingLinkLoading || isBookingLinkFetching ? (
              <PublicBookingSkeleton />
            ) : bookingLinkError || !bookingLink ? (
              <div className="p-8">
                <Alert
                  type="error"
                  showIcon
                  message="Unable to load this booking page"
                  description={getErrorMessage(
                    bookingLinkError,
                    "The booking link may be invalid or unavailable.",
                  )}
                />
              </div>
            ) : isRescheduleMode && !isRescheduleAllowedByLink ? (
              <div className="p-8">
                <Alert
                  type="error"
                  showIcon
                  message="Re-scheduling is unavailable"
                  description={rescheduleUnavailableMessage}
                />
              </div>
            ) : !isRescheduleMode &&
              bookingResultId &&
              bookingResultInviteeEmailError ? (
              <div className="p-8">
                <Alert
                  type="error"
                  showIcon
                  message="Unable to load this booking"
                  description={bookingResultInviteeEmailError}
                />
              </div>
            ) : !isRescheduleMode &&
              bookingResultId &&
              (isBookingResultDetailsLoading || isBookingResultDetailsFetching) ? (
              <PublicBookingSkeleton />
            ) : !isRescheduleMode &&
              bookingResultId &&
              bookingResultDetailsError ? (
              <div className="p-8">
                <Alert
                  type="error"
                  showIcon
                  message="Unable to load this booking"
                  description={getErrorMessage(
                    bookingResultDetailsError,
                    "This booking link may be invalid or unavailable.",
                  )}
                />
              </div>
            ) : !isRescheduleMode &&
              bookingResultId &&
              bookingResultDetails &&
              !confirmedBooking ? (
              <PublicBookingSkeleton />
            ) : isRescheduleMode &&
              (rescheduleBookingIdError || rescheduleInviteeEmailError) ? (
              <div className="p-8">
                <Alert
                  type="error"
                  showIcon
                  message="Unable to load this re-schedule page"
                  description={
                    rescheduleBookingIdError || rescheduleInviteeEmailError
                  }
                />
              </div>
            ) : isRescheduleMode &&
              (isBookingDetailsLoading || isBookingDetailsFetching) ? (
              <PublicBookingSkeleton />
            ) : isRescheduleMode && bookingDetailsError ? (
              <div className="p-8">
                <Alert
                  type="error"
                  showIcon
                  message="Unable to load this booking"
                  description={getErrorMessage(
                    bookingDetailsError,
                    "The re-schedule link may be invalid or unavailable.",
                  )}
                />
              </div>
            ) : isRescheduleMode && !isRescheduleAllowedForBooking ? (
              <div className="p-8">
                <Alert
                  type="error"
                  showIcon
                  message="Re-scheduling is unavailable"
                  description={rescheduleUnavailableMessage}
                />
              </div>
            ) : confirmedBooking ? (
              <PublicBookingConfirmation
                bookingLink={bookingLink}
                confirmation={confirmedBooking}
                timeFormat={timeFormat}
                mode={mode}
                isCancellingBooking={isCancellingBooking}
                onCancelBooking={openCancelModal}
                onBookAnother={() => {
                  setConfirmedBooking(null);
                  setSubmissionError(null);
                  setSelectedSlot(null);
                  setSelectedSlotKey(null);
                  setIsCancelModalOpen(false);
                  setCancelReason("");
                  setCancelReasonError(null);
                  if (publicKey && !isRescheduleMode) {
                    navigate(`/book/${encodeURIComponent(publicKey)}`, {
                      replace: true,
                    });
                  }
                }}
              />
            ) : selectedSlot ? (
              <BookingQuestionsForm
                key={[
                  selectedSlot.startTime,
                  selectedSlot.endTime,
                  rescheduleBookingDetails?.bookingUid,
                  rescheduleBookingDetails?.bookingId,
                  rescheduleBookingDetails?.id,
                ]
                  .filter((value) => typeof value !== "undefined" && value !== null)
                  .map(String)
                  .join(":")}
                bookingLink={bookingLink}
                selectedDate={selectedDate}
                selectedSlot={selectedSlot}
                timezone={timezone}
                timeFormat={timeFormat}
                isSubmitting={
                  isReservingSlot ||
                  isSendingEmailVerification ||
                  (isRescheduleMode ? isReschedulingBooking : isCreatingBooking)
                }
                submissionError={submissionError}
                mode={mode}
                existingBooking={rescheduleBookingDetails}
                onBack={handleBackToSlots}
                onConfirm={handleConfirmBooking}
              />
            ) : (
              <div className="grid min-h-0 grid-cols-1 lg:h-full lg:grid-cols-[300px_minmax(500px,1fr)_300px]">
                <EventDetailsPanel
                  bookingLink={bookingLink}
                  timezone={timezone}
                  canSelectTimezone={canSelectTimezone}
                  mode={mode}
                  existingBooking={rescheduleBookingDetails}
                  selectedDate={selectedSlot ? selectedDate : undefined}
                  selectedSlot={selectedSlot}
                  timeFormat={timeFormat}
                  onTimezoneChange={handleTimezoneChange}
                />
                <BookingCalendar
                  bookingLink={bookingLink}
                  selectedDate={selectedDate}
                  visibleMonth={visibleMonth}
                  availableDates={availableDatesSet}
                  isDatesLoading={
                    isAvailableDatesLoading || isAvailableDatesFetching
                  }
                  onMonthChange={(month) =>
                    setDateState((current) => ({
                      ...current,
                      publicKey,
                      visibleMonth: formatDateParam(month.startOf("month")),
                    }))
                  }
                  onDateSelect={handleDateSelect}
                />
                <AvailableSlotsPanel
                  selectedDate={selectedDate}
                  slots={availableSlots?.slots}
                  isLoading={
                    isAvailableDatesLoading ||
                    isAvailableDatesFetching ||
                    isSlotsLoading ||
                    isSlotsFetching
                  }
                  isReserving={isReservingSlot}
                  selectedSlotKey={selectedSlotKey}
                  timeFormat={timeFormat}
                  brandColor={
                    bookingLink?.workspace?.brandColor?.trim() || colors.primary
                  }
                  onTimeFormatChange={setTimeFormat}
                  onSlotSelect={handleSlotSelect}
                />
              </div>
            )}
          </section>

          <a
            href="https://notifyy.io"
            target="_blank"
            rel="noreferrer"
            aria-label="Powered by Notifyy"
            className={`inline-flex shrink-0 items-center gap-2 text-base font-semibold hover:underline sm:text-lg ${
              confirmedBooking ? "mt-0.5 sm:mt-1" : ""
            }`}
            style={{ color: colors.primaryDarkest }}
          >
            <img
              src={IMAGES.notifyyLogo}
              alt=""
              aria-hidden
              className="h-6 w-auto object-contain sm:h-7"
            />
            notifyy.io
          </a>
        </div>

        {slotsError && bookingLink && (
          <div className="mx-auto w-full max-w-290 shrink-0 pb-1">
            <Alert
              type="warning"
              showIcon
              message="Unable to load slots for the selected date"
              description={getErrorMessage(
                slotsError,
                "Please choose another date or try again.",
              )}
            />
          </div>
        )}
      </div>
      <Modal
        open={isCancelModalOpen}
        onClose={closeCancelModal}
        title="Cancel booking"
        subtitle="You can share a reason with the host. This is optional."
        variant="warning"
        size="medium"
        width={520}
        okText="Cancel booking"
        cancelText="Keep booking"
        onOk={handleCancelBooking}
        onCancel={closeCancelModal}
        confirmLoading={isCancellingBooking}
        maskClosable={!isCancellingBooking}
        keyboard={!isCancellingBooking}
        okButtonProps={{
          danger: true,
          disabled: cancelReason.trim().length > 500,
        }}
        cancelButtonProps={{
          disabled: isCancellingBooking,
        }}
        bodyPadding="24px 28px"
      >
        <TextAreaInput
          label="Reason"
          value={cancelReason}
          placeholder="Add a reason"
          rows={4}
          maxLength={500}
          showCount
          countInside
          autoFocus
          disabled={isCancellingBooking}
          error={cancelReasonError || undefined}
          onChange={(event) => {
            setCancelReason(event.target.value);
            setCancelReasonError(null);
          }}
        />
      </Modal>
      <Modal
        open={Boolean(pendingVerifiedBooking)}
        onClose={() => {
          if (isCreatingBooking || isSendingEmailVerification) return;
          setPendingVerifiedBooking(null);
          setVerificationCode("");
          setVerificationCodeError(null);
        }}
        title="Verify your email"
        subtitle={
          pendingVerifiedBooking ? (
            <span>
              Enter the code sent to{" "}
              <Link
                href={`mailto:${pendingVerifiedBooking.invitee.email}`}
                color={colors.primary}
                size="medium"
                underline="none"
                weight={700}
              >
                {pendingVerifiedBooking.invitee.email}
              </Link>
              .
            </span>
          ) : undefined
        }
        variant="info"
        size="medium"
        width={560}
        okText="Confirm booking"
        cancelText="Cancel"
        onOk={handleVerificationConfirm}
        onCancel={() => {
          setPendingVerifiedBooking(null);
          setVerificationCode("");
          setVerificationCodeError(null);
        }}
        confirmLoading={isCreatingBooking}
        maskClosable={!isCreatingBooking && !isSendingEmailVerification}
        keyboard={!isCreatingBooking && !isSendingEmailVerification}
        okButtonProps={{
          disabled:
            isSendingEmailVerification ||
            !verificationCode.trim() ||
            verificationCode.trim().length > 32,
        }}
        cancelButtonProps={{
          disabled: isCreatingBooking || isSendingEmailVerification,
        }}
        bodyPadding="28px 30px"
      >
        <div className="space-y-5">
          <Input
            label="Verification code"
            value={verificationCode}
            placeholder="Enter code"
            maxLength={32}
            autoFocus
            size="large"
            error={verificationCodeError || undefined}
            disabled={isCreatingBooking || isSendingEmailVerification}
            onChange={(event) => {
              setVerificationCode(event.target.value.trim());
              setVerificationCodeError(null);
            }}
            onPressEnter={handleVerificationConfirm}
          />
          <div className="space-y-1.5 text-sm leading-5">
            {verificationExpiryLabel ? (
              <p className="m-0" style={{ color: colors.textMuted }}>
                {verificationExpiryLabel}
              </p>
            ) : null}
            <Link
              href="#"
              color={colors.primary}
              size="medium"
              underline="none"
              weight={700}
              disabled={isCreatingBooking || isSendingEmailVerification}
              onClick={(event) => {
                event.preventDefault();
                resendVerificationCode();
              }}
            >
              {isSendingEmailVerification ? "Sending..." : "Send a new code"}
            </Link>
          </div>
        </div>
      </Modal>
    </main>
  );
};

export default PublicBookingPage;
